import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ProgressType } from './hooks/useTimeProgress';
import Tooltip from '@/components/Tooltip';

interface ProgressBarsProps {
    progress: ProgressType;
    isCountdown: boolean;
    lifeProgress?: number | null;
    onAddLifeBar?: () => void;
}

export const ProgressBars: React.FC<ProgressBarsProps> = ({
    progress,
    isCountdown,
    lifeProgress = null,
    onAddLifeBar,
}) => {
    const { t } = useTranslation('timePanel');

    // プログレスバーカラー設定
    const getProgressColor = (
        progressValue: number,
        type: 'year' | 'month' | 'week' | 'life',
    ): string => {
        if (type === 'life') {
            return 'bg-gray-500';
        }

        if (isCountdown) {
            // 今年の場合の特別な処理
            if (type === 'year' && [10, 20, 30, 40, 50, 60].includes(progressValue)) {
                return 'bg-blue-500';
            }
            // カウントダウンモードでは残り時間が少ない時に赤くする
            if (progressValue <= 15) return 'bg-red-500';
            if (progressValue <= 30) return 'bg-yellow-400 text-gray-800';
            return 'bg-gray-500';
        } else {
            // 今年の場合の特別な処理
            if (type === 'year' && [10, 20, 30, 40, 50, 60].includes(progressValue)) {
                return 'bg-blue-500';
            }
            // 通常モードでは進行度が高い時に赤くする
            if (progressValue >= 85) return 'bg-red-500';
            if (progressValue >= 70) return 'bg-yellow-400 text-gray-800';
            return 'bg-gray-500';
        }
    };

    const progressItems = [
        { key: 'week', value: progress.week, type: 'week' as const },
        { key: 'month', value: progress.month, type: 'month' as const },
        { key: 'year', value: progress.year, type: 'year' as const },
    ] as const;

    const extendedProgressItems =
        lifeProgress !== null && lifeProgress !== undefined
            ? [
                  ...progressItems,
                  {
                      key: 'life',
                      value: lifeProgress,
                      type: 'life' as const,
                  },
              ]
            : progressItems;

    const clampPercent = (value: number) => {
        if (Number.isNaN(value)) return 0;
        return Math.min(Math.max(value, 0), 100);
    };

    const displayValue = (value: number, type: 'week' | 'month' | 'year' | 'life') => {
        if (type === 'life') {
            // 小数点3桁で表示（1日で約0.003%変動）
            return value.toFixed(3);
        }
        return Math.round(value).toString();
    };

    const hasLifeBar = lifeProgress !== null && lifeProgress !== undefined;

    return (
        <div className="flex w-full flex-wrap gap-x-4 gap-y-6 lg:gap-4 justify-center">
            {extendedProgressItems.map(({ key, value, type }) => {
                const renderValue =
                    type === 'life'
                        ? clampPercent(isCountdown ? 100 - value : value)
                        : clampPercent(value);

                const isIntegerLife = type === 'life' && Number.isInteger(renderValue);

                return (
                    <div
                        key={key}
                        className="flex flex-col flex-[1_1_45%] sm:flex-[1_1_220px] max-w-[calc(50%-12px)] sm:max-w-[220px] lg:max-w-[260px] lg:px-2"
                        style={{ fontSize: '0.9em' }}
                    >
                        {/* プログレスバーのラベル */}
                        <div className="mb-2 flex items-center text-gray-500 justify-start">
                            <p className="text-left leading-none">{t(`progress.${key}`)}</p>
                        </div>
                        {/* プログレスバー */}
                        <div className="flex items-center lg:gap-4">
                            <div className="relative flex-1">
                                <div
                                    className="w-full min-h-[14px] min-w-[140px] bg-gray-200 rounded-full"
                                    style={{ height: '0.9em', minWidth: '140px' }}
                                    role="progressbar"
                                    aria-valuenow={renderValue}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                >
                                    <div
                                        className={`h-full rounded-full ${getProgressColor(renderValue, type)}`}
                                        style={{ width: `${renderValue}%` }}
                                    />
                                    {/* パーセンテージ表示: 25%以下はバー中央に濃いグレー、それ以外はバー内に白 */}
                                    <span
                                        className={`absolute inset-0 flex items-center px-1 ${
                                            renderValue <= 25
                                                ? 'justify-center text-gray-700'
                                                : 'justify-start text-white'
                                        } ${isIntegerLife ? 'text-yellow-400 font-bold' : ''}`}
                                        style={renderValue > 25 ? { width: `${renderValue}%`, justifyContent: 'center' } : undefined}
                                    >
                                        {displayValue(renderValue, type)}%
                                    </span>
                                </div>
                            </div>
                            {type === 'year' && !hasLifeBar && onAddLifeBar && (
                                <Tooltip content={t('lifeBar.add')}>
                                    <button
                                        type="button"
                                        onClick={onAddLifeBar}
                                        className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 ms-4 text-xs text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                                        aria-label={t('lifeBar.add')}
                                    >
                                        <svg
                                            className="h-3.5 w-3.5"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                        >
                                            <path d="M12 5v14M5 12h14" />
                                        </svg>
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
